import React from 'react';
import { Control, Controller, useFieldArray } from 'react-hook-form';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { Tooltip } from '../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Icon } from '../../../../../../CommonComponents/OPL/Icon/Icon';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { HtmlTable } from '../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { InputNumberMask } from '../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { IconButton } from '../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { BusinessAssetsFormValues } from '../../BusinessAssetsForm.meta';

interface Props {
  control: Control<BusinessAssetsFormValues>;
}

export const NetProceedsFromSale = ({ control }: Props) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'BusinessSaleSchedule',
  });

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        borderColor="neutral.200"
        borderStyle="solid"
        borderWidth="1px 0"
        px="24"
        py="12"
      >
        <Text fontWeight={600}>
          Net Proceeds From Sale{' '}
          <Tooltip
            content="Enter the estimated proceeds from the sale of this asset minus taxes and any fees to sell it. If you will receive additional years of proceeds, also add the impact to your net worth. For example, if after year 1 you will still own 50% of the business and after year 2 you will own 0%, please add 50% for year 1 and 0% for year 2 in the Net Worth Column."
            placement="right"
          >
            <Icon className="far fa-info-circle" color="neutral.500" fontSize="small" ml="4" />
          </Tooltip>
        </Text>

        <Button
          leftIcon="fas fa-plus"
          variant="link"
          onClick={() =>
            append({
              YearsAfterSaleYear: fields.length,
              SaleAmount: null,
              NetWorthPercent: null,
            })
          }
        >
          Add Year
        </Button>
      </Box>

      <HtmlTable variant="embedded" columnSpacing="small" edgePadding={24}>
        <table style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 72 }} />
            <col style={{ width: 275 }} />
            <col />
            <col style={{ width: 72 }} />
          </colgroup>

          <thead>
            <tr>
              <th>
                <Text variant="smallCaps">Year</Text>
              </th>
              <th>
                <Text variant="smallCaps" textAlign="left">
                  Amount
                </Text>
              </th>
              <th>
                <Text variant="smallCaps" textAlign="left">
                  Net Worth
                </Text>
              </th>
              <th>&nbsp;</th>
            </tr>
          </thead>

          <tbody>
            {fields.map((row, index) => (
              <Box as="tr" key={row.id}>
                <Text as="td" fontWeight={500} textAlign="center">
                  {row.YearsAfterSaleYear + 1}
                </Text>

                <td>
                  <Controller
                    control={control}
                    name={`BusinessSaleSchedule.${index}.SaleAmount`}
                    render={({ field: { onChange, ...field } }) => (
                      <InputNumberMask
                        leftIcon="far fa-dollar-sign"
                        decimalScale={2}
                        allowNegative={false}
                        onValueChange={({ floatValue }) => {
                          onChange(floatValue ?? null);
                        }}
                        aria-label="Sale Amount"
                        {...field}
                      />
                    )}
                  />
                </td>

                <td>
                  <Controller
                    control={control}
                    name={`BusinessSaleSchedule.${index}.NetWorthPercent`}
                    render={({ field: { onChange, ...field } }) => (
                      <InputNumberMask
                        rightIcon="far fa-percent"
                        decimalScale={2}
                        allowNegative={false}
                        onValueChange={({ floatValue }) => {
                          onChange(floatValue ?? null);
                        }}
                        aria-label="Net Worth"
                        {...field}
                      />
                    )}
                  />
                </td>

                <td style={{ textAlign: 'center' }}>
                  {index > 0 && index === fields.length - 1 && (
                    <IconButton
                      onClick={() => remove(index)}
                      icon="far fa-minus-circle"
                      variant="subtleInactive"
                      aria-label="Remove year"
                    />
                  )}
                </td>
              </Box>
            ))}
          </tbody>
        </table>
      </HtmlTable>
    </>
  );
};
